import { matchPath } from 'react-router-dom';

export const FEATURE_BADGE_VERSION = 'v0.42';

export interface PageBadgeContext {
  pathname: string;
  search?: string;
  hostname?: string;
  isDev?: boolean;
  isE2E?: boolean;
  debugFlag?: string;
  viewMode?: 'analytical' | 'broadcast';
}

export interface PageBadgeInfo {
  pageName: string;
  versionLabel: string;
}

interface PageRoute {
  pattern: string;
  name: string | ((context: PageBadgeContext) => string);
}

const PAGE_ROUTES: PageRoute[] = [
  { pattern: '/', name: 'Race Directory' },
  { pattern: '/landing', name: 'Home' },
  { pattern: '/races', name: 'Race Directory' },
  { pattern: '/regatta/:slug', name: 'Regatta Overview' },
  { pattern: '/regatta/:slug/upload', name: 'Track Upload' },
  { pattern: '/regatta/:slug/:div/race/:num', name: viewerName },
  { pattern: '/view/:raceId', name: viewerName },
  { pattern: '/replay/races/:raceId/report', name: 'Race Report' },
  { pattern: '/profile', name: 'Racing Profile' },
  { pattern: '/invite/:token', name: 'Invitation' },
  { pattern: '/claim/track/:token', name: 'Track Claim' },
  { pattern: '/login', name: 'Sign In' },
  { pattern: '/auth/verify', name: 'Sign In Verification' },
  { pattern: '/terms', name: 'Terms' },
  { pattern: '/privacy', name: 'Privacy' },
  { pattern: '/admin', name: 'Admin Dashboard' },
  { pattern: '/admin/inbox', name: 'Track Inbox' },
  { pattern: '/admin/users', name: 'User Admin' },
  { pattern: '/admin/regattas/:regattaId/entries', name: 'Entry Admin' },
  { pattern: '/admin/regattas/:regattaId/documents/:docId/review-entries', name: 'Entry Review' },
  { pattern: '/admin/races/:raceId', name: 'Race Admin' },
  { pattern: '/admin/sessions/new', name: 'Session Create' },
  { pattern: '/admin/sessions/:sessionId', name: 'Session Overview' },
  { pattern: '/admin/sessions/:sessionId/race/:raceId', name: viewerName },
  { pattern: '/admin/sessions/:sessionId/edit', name: sessionEditorName },
];

export function resolvePageBadgeInfo(context: PageBadgeContext): PageBadgeInfo {
  const route = PAGE_ROUTES.find((candidate) =>
    matchPath({ path: candidate.pattern, end: true }, context.pathname)
  );
  const pageName = route
    ? typeof route.name === 'function'
      ? route.name(context)
      : route.name
    : 'Not Found';

  return {
    pageName,
    versionLabel: formatPageBuildLabel(FEATURE_BADGE_VERSION, __APP_GIT_SHA__),
  };
}

export function shouldShowPageBadge(context: PageBadgeContext): boolean {
  if (context.debugFlag === 'true') return true;
  if (context.isE2E) return false;
  if (context.isDev) return true;

  // #1031: staging builds only — the badge never renders in production
  // (the old prod /admin exception is gone per Jason's direction).
  const host = context.hostname ?? '';
  return host.startsWith('staging.') || host.includes('.staging.');
}

export function formatPageBuildLabel(featureVersion: string, gitSha: string): string {
  const normalizedSha = gitSha.trim() || 'local';
  return `${featureVersion} (${normalizedSha})`;
}

function viewerName(context: PageBadgeContext) {
  return context.viewMode === 'broadcast' ? 'Broadcast Viewer' : 'Analytical Viewer';
}

function sessionEditorName(context: PageBadgeContext) {
  const params = new URLSearchParams(context.search ?? '');
  return params.has('race') ? 'Race Editor' : 'Race Selector';
}
